import React, { useState, useEffect, useCallback, memo } from "react";
import { useSelector, useDispatch, batch } from "react-redux";
import {
  Divider,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  CloseButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
} from "@chakra-ui/react";
import { MediaOnlySidebarPanel } from "../../../_components/common/statelessView";
import ImagesList from "./ImagesList";
import GiphyList from "./GiphyList";
import AssetsList from "./AssetsList";
import ProjectsList from "./ProjectsList";
import { setMediaSidebarPanelOpen } from "../../../_actions/plannerActions";
import {
  resetPage,
  switchMediaTab,
  closeSlider,
} from "../../../_actions/sidebarSliderActions";
import { analyticsTrackEvent } from "../../../_utils/common";
import {
  GIPHY_SLIDER_PANEL,
  IMAGES_SLIDER_PANEL,
  // VIDEO_SLIDER_PANEL,
  ASSETS_SLIDER_PANEL,
  PROJECTS_SLIDER_PANEL,
  TAB_CALENDAR_MEDIA,
  FOLDERS_SLIDER_PANEL,
} from "../../../_components/details/constants";
import ExportStoryModal from "../ExportStoryModal";
import {
  getAssetPostEditorPayload,
  getGiphyPostEditorPayload,
  getImagePostEditorPayload,
  getProjectPostEditorPayload,
  // getVideoPostEditorPayload,
  setPostEditorDragData,
} from "../../utils";
import { FaClock, FaFolder, FaFolderOpen, FaImage, FaPhotoVideo } from "react-icons/fa";
import FoldersSliderPanel from "apps/simplified-web/src/_components/details/sidebarPanels/foldersPanel/FoldersSliderPanel";
import { translate as trans } from "../../../_components/social/Trans/Trans";

const CustomTab = memo(({ children, icon, ...props }) => (
  <Tab {...props} as="button" className="media-only-panel-tab">
    <div className={"media-only-panel-tab-icon"}>{icon}</div>
    <span className="media-only-panel-tab-title">{children}</span>
  </Tab>
));

const TldrMediaSidebarPanel = ({ onAddFile }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [mediaTabIndex, setMediaTabIndex] = useState(0);
  const [exportStory, setExportStory] = useState(null);
  const dispatch = useDispatch();
  const sidebarSlider = useSelector((state) => state.sidebarSlider);

  const getMediaTypeFromIndex = useCallback((index) => {
    const mediaTypeTabs = [
      PROJECTS_SLIDER_PANEL,
      ASSETS_SLIDER_PANEL,
      IMAGES_SLIDER_PANEL,
      GIPHY_SLIDER_PANEL,
      FOLDERS_SLIDER_PANEL,
    ];

    return mediaTypeTabs[index] || mediaTypeTabs[0];
  }, []);

  const handleMediaSidebarPanelClose = useCallback(() => {
    dispatch(setMediaSidebarPanelOpen(false));
  }, [dispatch]);

  const onSelectTabChange = useCallback(
    (index) => {
      batch(() => {
        dispatch(resetPage(sidebarSlider.searchText));
        dispatch(switchMediaTab(getMediaTypeFromIndex(index)));
      });

      setTabIndex(index);
    },
    [setTabIndex, dispatch, sidebarSlider?.searchText, getMediaTypeFromIndex]
  );

  const handleExportStoryEnd = useCallback(
    (data) => {
      setExportStory(null);

      const files = data?.urls || [];

      for (let i = 0; i < files.length; i++) {
        const postEditorPayload = getProjectPostEditorPayload({
          file: files[i],
        });

        onAddFile?.(postEditorPayload);
      }
    },
    [setExportStory, onAddFile]
  );

  const handleProjectItemClick = useCallback(
    (data) => {
      setExportStory(data);
    },
    [setExportStory]
  );

  // const handleProjectItemDrag = useCallback(
  //   (event, data) => {
  //     setExportStory(data);
  //   },
  //   [setExportStory]
  // );

  const handleAssetItemClick = useCallback(
    (data) => {
      const postEditorPayload = getAssetPostEditorPayload(data);
      onAddFile?.(postEditorPayload);

      analyticsTrackEvent("attachedProject", {
        projectId: data.id,
        projectType: postEditorPayload.mimeType.split("/").pop(),
      });
    },
    [onAddFile]
  );

  const handleAssetItemDrag = useCallback((event, data) => {
    const postEditorPayload = getAssetPostEditorPayload(data);
    setPostEditorDragData(event, postEditorPayload);
  }, []);

  const handleImageItemClick = useCallback(
    (data) => {
      const postEditorPayload = getImagePostEditorPayload(data);
      onAddFile?.(postEditorPayload);
    },
    [onAddFile]
  );

  const handleImageItemDrag = useCallback((event, data) => {
    const postEditorPayload = getImagePostEditorPayload(data);
    setPostEditorDragData(event, postEditorPayload);
  }, []);

  const handleTemplateItemClick = useCallback(
    (data) => {
      const payload = {
        ...data,
        payload: {
          src: data.content.meta.image,
          height: data.content.meta.image_height,
          width: data.content.meta.image_width,
          type: data.type,
          name: data.content.meta.title,
        }
      };
      const postEditorPayload = getAssetPostEditorPayload(payload);
      onAddFile?.(postEditorPayload);

      analyticsTrackEvent("attachedProject", {
        projectId: data.id,
        projectType: postEditorPayload.mimeType.split("/").pop(),
      });
    },
    [onAddFile]
  );

  // const handleVideoItemClick = useCallback(
  //   (data) => {
  //     const postEditorPayload = getVideoPostEditorPayload(data);
  //     onAddFile?.(postEditorPayload);
  //   },
  //   [onAddFile]
  // );

  // const handleVideoItemDrag = useCallback((event, data) => {
  //   const postEditorPayload = getVideoPostEditorPayload(data);
  //   setPostEditorDragData(event, postEditorPayload);
  // }, []);

  const handleGiphyItemClick = useCallback(
    (data) => {
      const postEditorPayload = getGiphyPostEditorPayload(data);
      onAddFile?.(postEditorPayload);
    },
    [onAddFile]
  );

  const handleGiphyItemDrag = useCallback((event, data) => {
    const postEditorPayload = getGiphyPostEditorPayload(data);
    setPostEditorDragData(event, postEditorPayload);
  }, []);

  useEffect(() => {
    dispatch(switchMediaTab(getMediaTypeFromIndex(0)));

    return () => {
      dispatch(closeSlider());
    };
  }, []);

  return (
    <MediaOnlySidebarPanel onClose={handleMediaSidebarPanelClose}>
      <CloseButton
        className="media-only-panel-close-btn"
        size="md"
        onClick={handleMediaSidebarPanelClose}
      />

      <Tabs
        h="100%"
        display="flex"
        flexDirection="column"
        isLazy
        defaultIndex={0}
        colorScheme="primary"
        index={tabIndex}
        onChange={onSelectTabChange}
      >
        <TabList className="media-only-panel-tablist">
          <CustomTab icon={<FaClock />}>Projects</CustomTab>
          <CustomTab icon={<FaFolderOpen />}>Assets</CustomTab>
          <CustomTab icon={<FaImage />}>Photos</CustomTab>
          <CustomTab icon={<FaImage />}>GIFs</CustomTab>
          <CustomTab icon={<FaFolder />}>Folders</CustomTab>
        </TabList>

        <Divider my={4} width={"calc(100% - 24px)"} borderColor="white" opacity="0.2" ml="4px"/>

        <TabPanels flexGrow="1">
          <TabPanel className="media-only-panel-tab-panel">
            <ProjectsList
              onItemClick={handleProjectItemClick}
            // onItemDrag={handleProjectItemDrag}
            />
          </TabPanel>
          <TabPanel className="media-only-panel-tab-panel">
            <AssetsList
              onItemClick={handleAssetItemClick}
              onItemDrag={handleAssetItemDrag}
            />
          </TabPanel>
          <TabPanel className="media-only-panel-tab-panel">
            <ImagesList
              onItemClick={handleImageItemClick}
              onItemDrag={handleImageItemDrag}
            />
          </TabPanel>
          <TabPanel className="media-only-panel-tab-panel">
            <GiphyList
              onItemClick={handleGiphyItemClick}
              onItemDrag={handleGiphyItemDrag}
            />
          </TabPanel>
          <TabPanel className="media-only-panel-tab-panel">
            <FoldersSliderPanel
              isDragAndDropContextAvailable={false}
              storyCardTab={TAB_CALENDAR_MEDIA}
              onAssetClick={handleAssetItemClick}
              onProjectClick={handleProjectItemClick}
              onTemplateClick={handleTemplateItemClick}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {exportStory ? (
        <Modal
          autoFocus={false}
          returnFocusOnClose={false}
          isOpen
          size="md"
          onClose={() => setExportStory(null)}
        >
          <ModalOverlay />
          <ModalContent position="relative">
            <ModalHeader>{trans("planner.export_story.header")}</ModalHeader>
            <ModalBody>
              <ExportStoryModal
                story={{
                  payload: exportStory,
                  contentSize: {
                    width: exportStory?.image_width,
                    height: exportStory?.image_height,
                  },
                }}
                onExportEnd={handleExportStoryEnd}
                showHeader={false}
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      ) : null}
    </MediaOnlySidebarPanel>
  );
};

TldrMediaSidebarPanel.defaultProps = {};

export default memo(TldrMediaSidebarPanel);
